import * as _ from 'lodash';
import * as hash from 'object-hash';
import { Component } from 'react';
import { Dispatch, connect } from "react-redux";
import { AppActionCreators, IAppState } from '@nkr/store/application';
import { DonorActionCreators, FileActionCreators, IApplicationState, IDonorProfilePostTransplantResponse } from '@nkr/store/index';
import { IBaseResponse, IDonorProfileHeaderInfo, IDonorProfilePostTransplant } from '@nkr/models/index';
import { toast } from 'react-toastify';
import { IBaseDispatchProps, ToggleType } from '../models';
import Content from './content';
import { FileDocumentType, FileDocumentTypeDescription } from '@nkr/enums/index';
import { downloadFile } from '@nkr/services/download-file';

interface IOwnProps {
    centerId: number,
    activeKey: string,
    isAdmin: boolean,
    donorId: number,
    headerInfo: IDonorProfileHeaderInfo,
    hasDataChanged: boolean,
    saveRequested: boolean,
    refreshRequested: boolean,
    onDataChanged: (hasDataChanged: boolean) => void,
    onSaveFinished: (refreshHeader: boolean) => void,
    onRefreshFinished: () => void,
    onResetDataChangedWarning: () => void,
    onTabClick: (href: string) => void,
    onUpdateForErrors: (toggleType: ToggleType, messages: string[]) => void
}

interface IStateProps {
    app: IAppState,
    groupIsSuper: boolean
}

interface IDispatchProps extends IBaseDispatchProps {
    getProfilePostTransplant: (centerId: number, donorId: number) => Promise<IDonorProfilePostTransplantResponse>,
    saveProfilePostTransplant: (centerId: number, donorId: number, whichKidneyDonated: string, crossClampDateTime: string, perfusionCompletionDateTime: string, completedFormsAndChecklists: File) => Promise<IBaseResponse>,
    downloadFile: (docType: number, lookupId: number) => Promise<any>,
}

type TProps = IStateProps & IDispatchProps & IOwnProps;

interface IState {
    data: IDonorProfilePostTransplant,
    newCompletedFormsAndChecklistsFile: File,
    donorDataHash: string,
}

const initialState: IState = {
    data: null,
    newCompletedFormsAndChecklistsFile: null,
    donorDataHash: '',
}

class PostTransplant extends Component<TProps, IState> {
    readonly state: IState = initialState;

    componentDidMount = (): void => {
        this.getData();
    }

    componentWillReceiveProps = (nextProps: Readonly<TProps>, nextContext: any) => {
        if (this.props.saveRequested == false && nextProps.saveRequested == true) {
            this.handleSave();
        }

        if (this.props.refreshRequested == false && nextProps.refreshRequested == true) {
            this.getData();
        }
    }

    getData = () => {
        this.props.showLoading('Getting post transplant data, one moment please...').then(() => {
            Promise.all([
                this.props.getProfilePostTransplant(this.props.centerId, this.props.donorId)
            ])
                .then(([response]) => {
                    if (response.data.success) {
                        this.setState({
                            data: response.data.results,
                            donorDataHash: hash(response.data.results)
                        }, () => {
                            if (this.props.refreshRequested) {
                                this.props.onRefreshFinished();
                            }
                        });
                    }
                })
                .catch(error => {
                })
                .finally(() => {
                    this.props.hideLoading();
                });
        });
    }

    handleDataChange = (data: IDonorProfilePostTransplant) => {
        let newData = _.cloneDeep(data);
        let newHash = hash(newData);

        let hasDataChanged = newHash != this.state.donorDataHash;

        this.setState({
            data: newData,
        });

        this.props.onDataChanged(hasDataChanged);
    }

    handleChangeFile = (newCompletedFormsAndChecklistsFile: File) => {
        this.setState({
            newCompletedFormsAndChecklistsFile,
        });

        this.props.onDataChanged(true);
    }

    handleRemoveFile = () => {
        let { donorDataHash, data } = this.state;

        let newData = _.cloneDeep(data);
        let newHash = hash(newData);

        this.setState({
            newCompletedFormsAndChecklistsFile: null,
        });

        if (donorDataHash == newHash) {
            this.props.onDataChanged(false);
        }
    }

    handleViewFile = () => {
        this.props.downloadFile(FileDocumentType.DonorPostTransplantFormsChecklists, this.props.donorId).then((response: any) => {
            downloadFile(response.data, `${FileDocumentTypeDescription[FileDocumentType.DonorPostTransplantFormsChecklists]}.pdf`, response.headers.contentType)
        }).catch((error: IBaseResponse) => {
            toast.error("Unable to download file at this time.  Please contact support.");
        });
    }

    handleSave = () => {
        this.props.showLoading('Saving kidney data, one moment please...').then(() => {
            this.props.saveProfilePostTransplant(this.props.centerId, this.props.donorId, this.state.data.whichKidneyDonated, this.state.data.crossClampDateTime, this.state.data.perfusionCompletionDateTime, this.state.newCompletedFormsAndChecklistsFile)
                .then(response => {
                    if (response.data.success) {
                        let donorDataHash = hash(this.state.data);

                        this.setState({
                            donorDataHash,
                            newCompletedFormsAndChecklistsFile: null,
                        }, () => {
                            this.props.onDataChanged(false);
                            this.props.onResetDataChangedWarning();
                            this.props.onUpdateForErrors(ToggleType.Success, []);
                            this.props.onSaveFinished(false);
                        });
                    }
                })
                .catch(error => {
                    toast.error(error.response.data);
                })
                .finally(() => {
                    this.props.hideLoading();
                });
        });
    }

    render = () => {
        if (this.props.headerInfo == null)
            return null;

        let { data } = this.state;

        if (data == null) {
            return null;
        }

        return (
            <Content
                data={data}
                newCompletedFormsAndChecklistsFile={this.state.newCompletedFormsAndChecklistsFile}
                handleDataChange={this.handleDataChange}
                onChangeFile={this.handleChangeFile}
                onRemoveFile={this.handleRemoveFile}
                onViewFile={this.handleViewFile}
            />
        );
    }
}

const mapStateToProps = (appState: IApplicationState): IStateProps => {
    return {
        app: appState.app,
        groupIsSuper: appState.app.user.group.isSuper
    }
}

const mapDispatchToProps = (dispatch: Dispatch<IApplicationState>): IDispatchProps => {
    return {
        showLoading: (message) => {
            return dispatch(AppActionCreators.showLoadingAsync(message));
        },
        hideLoading: () => {
            dispatch(AppActionCreators.hideLoading());
        },
        getProfilePostTransplant: (centerId, donorId) => {
            return dispatch(DonorActionCreators.getProfilePostTransplant(centerId, donorId))
        },
        saveProfilePostTransplant: (centerId, donorId, whichKidneyDonated, crossClampDateTime, perfusionCompletionDateTime, completedFormsAndChecklists) => {
            return dispatch(DonorActionCreators.saveProfilePostTransplant(centerId, donorId, whichKidneyDonated, crossClampDateTime, perfusionCompletionDateTime, completedFormsAndChecklists))
        },
        downloadFile: (docType, id) => {
            return dispatch(FileActionCreators.downloadFile(docType, id))
        },
    };
}

export default connect<IStateProps, IDispatchProps, IOwnProps>(
    mapStateToProps,
    mapDispatchToProps)(PostTransplant);